import { useEffect, useMemo, useState } from "react";
import { X, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateTimeField } from "@/components/datetime";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatTime12 } from "@/lib/utils";

function tomorrowDate() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export default function BookFollowUpDialog({ open, appointment, onClose, onBooked }) {
  const [date, setDate] = useState(tomorrowDate());
  const [reason, setReason] = useState("Follow-up visit");
  const [slots, setSlots] = useState(null);
  const [slotLoading, setSlotLoading] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedSlotKey, setSelectedSlotKey] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDate(tomorrowDate());
    setReason("Follow-up visit");
    setSlots(null);
    setSelectedSessionId("");
    setSelectedSlotKey("");
    setBusy(false);
  }, [open, appointment?.id]);

  useEffect(() => {
    if (!open || !appointment || !date) return;
    setSlotLoading(true);
    setSelectedSessionId("");
    setSelectedSlotKey("");
    const params = new URLSearchParams({ date });
    if (appointment.location_id && appointment.location_id !== "default") params.set("location_id", appointment.location_id);
    if (appointment.doctor_id) params.set("doctor_id", appointment.doctor_id);
    api.get(`/appointments/slots?${params.toString()}`)
      .then(({ data }) => {
        setSlots(data);
        const firstAvailable = (data.sessions || []).find((s) => s.available && !s.is_past) || (data.sessions || [])[0];
        setSelectedSessionId(firstAvailable?.id || "");
      })
      .catch((err) => {
        setSlots(null);
        toast.error(formatApiErrorDetail(err) || "Could not check follow-up availability");
      })
      .finally(() => setSlotLoading(false));
  }, [open, appointment, date]);

  const selectedSession = useMemo(() => {
    return (slots?.sessions || []).find((s) => s.id === selectedSessionId) || null;
  }, [slots, selectedSessionId]);

  const isSlotMode = selectedSession?.booking_mode === "slot_based";
  const selectedSlot = useMemo(() => {
    if (!isSlotMode) return null;
    return (selectedSession?.slots || []).find((s) => `${s.start_time}-${s.end_time}` === selectedSlotKey) || null;
  }, [isSlotMode, selectedSession, selectedSlotKey]);

  const canBook = !!appointment && !!date && !!selectedSession && !selectedSession.is_past && (
    isSlotMode ? !!selectedSlot?.available : (selectedSession.follow_up_slots_left > 0 || selectedSession.slots_left > 0)
  );

  const submit = async () => {
    if (!appointment) return;
    if (!appointment.patient_phone) {
      toast.error("Patient phone number is missing. Add phone number before booking follow-up.");
      return;
    }
    if (!canBook) {
      toast.error("Please choose an available follow-up session.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        patient_name: appointment.patient_name,
        patient_phone: appointment.patient_phone,
        reason: reason.trim() || "Follow-up visit",
        queue_date: date,
        booking_type: "follow_up",
        doctor_id: appointment.doctor_id,
        location_id: appointment.location_id && appointment.location_id !== "default" ? appointment.location_id : undefined,
        queue_session_id: selectedSession.id,
        slot_start_time: isSlotMode ? selectedSlot.start_time : undefined,
        slot_end_time: isSlotMode ? selectedSlot.end_time : undefined,
        slot_label: isSlotMode ? selectedSlot.label : undefined,
        source_appointment_id: appointment.id,
      };
      const { data } = await api.post("/appointments/walkin", payload, { timeout: 30000 });
      toast.success("Follow-up booked");
      onBooked?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not book follow-up");
    } finally {
      setBusy(false);
    }
  };

  if (!open || !appointment) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-card border border-border p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Follow-up booking</div>
            <h2 className="font-display font-bold text-xl text-foreground">Book follow-up</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {appointment.patient_name} · Dr {appointment.doctor_name || "Doctor"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 hover:bg-muted transition-colors">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-semibold text-foreground">Future date</Label>
            <DateTimeField
              mode="date"
              value={date}
              onChange={setDate}
              minDate={tomorrowDate()}
              placeholder="Choose follow-up date"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-foreground">Reason</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} className="h-11 rounded-xl mt-1" placeholder="Follow-up visit" />
          </div>

          {slotLoading ? (
            <div className="rounded-xl border border-border bg-background p-4 text-sm text-muted-foreground">Checking availability...</div>
          ) : slots ? (
            <>
              <div className={`rounded-xl border p-3 text-sm ${
                slots.clinic_open && slots.is_working_day && !slots.is_blocked_date
                  ? "border-primary/20 bg-primary/[0.03] text-primary"
                  : "border-destructive/20 bg-destructive/[0.03] text-destructive"
              }`}>
                {slots.message || (slots.is_working_day ? `${slots.slots_left} slot${slots.slots_left !== 1 ? "s" : ""} available.` : "Doctor is not working on this date.")}
              </div>

              {slots.sessions?.length > 0 && (
                <div>
                  <Label className="text-sm font-semibold text-foreground">Session</Label>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {slots.sessions.map((s) => {
                      const active = selectedSessionId === s.id;
                      const left = s.follow_up_slots_left ?? s.slots_left ?? 0;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          disabled={s.is_past}
                          onClick={() => { setSelectedSessionId(s.id); setSelectedSlotKey(""); }}
                          className={`rounded-xl border px-3 py-2 text-left text-sm transition-colors ${
                            active
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border bg-background text-muted-foreground hover:bg-muted"
                          } ${s.is_past ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          <div className="font-semibold text-foreground">{s.label || "Clinic"}</div>
                          <div className="text-xs">
                            {formatTime12(s.start_time?.slice(0, 5))} - {formatTime12(s.end_time?.slice(0, 5))} · {left} follow-up left
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {isSlotMode && (
                <div>
                  <Label className="text-sm font-semibold text-foreground">Time slot</Label>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
                    {(selectedSession?.slots || []).map((slot) => {
                      const key = `${slot.start_time}-${slot.end_time}`;
                      const active = selectedSlotKey === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          disabled={!slot.available}
                          onClick={() => setSelectedSlotKey(key)}
                          className={`rounded-xl border px-3 py-2 text-xs font-semibold transition-colors ${
                            active
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border bg-background text-foreground hover:bg-muted"
                          } ${!slot.available ? "opacity-45 cursor-not-allowed line-through" : ""}`}
                        >
                          <Clock size={12} weight="duotone" className="inline mr-1" />
                          {slot.start_time?.slice(0, 5)} - {slot.end_time?.slice(0, 5)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full h-11 flex-1">Cancel</Button>
          <Button type="button" onClick={submit} disabled={busy || !canBook} className="rounded-full h-11 flex-1 bg-primary text-primary-foreground hover:bg-[#1a3025]">
            {busy ? "Booking..." : "Book follow-up"}
          </Button>
        </div>
      </div>
    </div>
  );
}
